import React, { useState, useMemo } from 'react';
import { StreakDisplay } from './StreakDisplay';
import { StudyGoals } from './StudyGoals';
import { AchievementsList } from './AchievementsList';
import { ProgressCharts } from './ProgressCharts';
import { ReviewForecast } from './ReviewForecast';
import './Statistics.css';

export const Statistics = ({
  allDecks,
  reviewData,
  memorized,
  favorites,
  theme,
  currentStreak,
  longestStreak,
  todayStudied,
  dailyGoal,
  weeklyGoal,
  dailyProgress,
  weeklyProgress,
  dailyGoalMet,
  weeklyGoalMet,
  dailyProgressPercent,
  weeklyProgressPercent,
  onSetDailyGoal,
  onSetWeeklyGoal,
  achievements,
  unlockedAchievements,
  unlockedCount,
  totalCount,
  getProgress,
  onClose
}) => {
  const [selectedDeck, setSelectedDeck] = useState('all');

  // Calculate overall statistics
  const stats = useMemo(() => {
    const deckKeys = Object.keys(allDecks);
    let totalCards = 0;
    let totalReviewed = 0;
    let totalReviewCount = 0;
    let ratingCounts = { again: 0, hard: 0, medium: 0, easy: 0 };
    let newCards = 0;
    let learningCards = 0;
    let matureCards = 0;
    let dueCards = 0;
    const deckStats = {};

    deckKeys.forEach(deckName => {
      const cards = allDecks[deckName];
      const deckCardCount = cards.length;
      totalCards += deckCardCount;

      let deckReviewed = 0;
      let deckReviewCount = 0;
      let deckRatings = { again: 0, hard: 0, medium: 0, easy: 0 };
      let deckNew = 0;
      let deckLearning = 0;
      let deckMature = 0;
      let deckDue = 0;

      cards.forEach((card, index) => {
        const key = `${deckName}|${index}`;
        const review = reviewData[key];

        if (review && review.reviewCount > 0) {
          deckReviewed++;
          totalReviewed++;
          deckReviewCount += review.reviewCount;
          totalReviewCount += review.reviewCount;

          if (review.rating) {
            deckRatings[review.rating]++;
            ratingCounts[review.rating]++;
          }

          // Categorize by interval
          if (review.interval < 21) {
            deckLearning++;
            learningCards++;
          } else {
            deckMature++;
            matureCards++;
          }

          // Check if due
          if (Date.now() >= review.dueDate) {
            deckDue++;
            dueCards++;
          }
        } else {
          deckNew++;
          newCards++;
          deckDue++;
          dueCards++;
        }
      });

      deckStats[deckName] = {
        total: deckCardCount,
        reviewed: deckReviewed,
        reviewCount: deckReviewCount,
        ratings: deckRatings,
        new: deckNew,
        learning: deckLearning,
        mature: deckMature,
        due: deckDue,
        accuracy: deckReviewCount > 0
          ? ((deckRatings.easy + deckRatings.medium) / deckReviewCount * 100).toFixed(1)
          : 0
      };
    });

    const accuracy = totalReviewCount > 0
      ? ((ratingCounts.easy + ratingCounts.medium) / totalReviewCount * 100).toFixed(1)
      : 0;

    return {
      totalCards,
      totalReviewed,
      totalReviewCount,
      ratingCounts,
      newCards,
      learningCards,
      matureCards,
      dueCards,
      accuracy,
      deckStats,
      memorizedCount: memorized.length,
      favoritesCount: favorites.length
    };
  }, [allDecks, reviewData, memorized, favorites]);

  // Get recent reviews
  const recentReviews = useMemo(() => {
    const reviews = [];

    Object.entries(reviewData).forEach(([key, review]) => {
      if (review && review.lastReviewed) {
        const [deckName, indexStr] = key.split('|');
        const index = parseInt(indexStr);

        if (allDecks[deckName] && allDecks[deckName][index]) {
          const card = allDecks[deckName][index];
          reviews.push({
            deckName,
            card,
            review,
            timestamp: review.lastReviewed
          });
        }
      }
    });

    return reviews
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 10);
  }, [reviewData, allDecks]);

  const displayStats = selectedDeck === 'all'
    ? stats
    : {
        ...stats.deckStats[selectedDeck],
        totalReviewed: stats.deckStats[selectedDeck]?.reviewed || 0,
        totalReviewCount: stats.deckStats[selectedDeck]?.reviewCount || 0,
        ratingCounts: stats.deckStats[selectedDeck]?.ratings || { again: 0, hard: 0, medium: 0, easy: 0 },
        totalCards: stats.deckStats[selectedDeck]?.total || 0
      };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getRatingColor = (rating) => {
    const colors = {
      again: '#ef4444',
      hard: '#f97316',
      medium: '#22c55e',
      easy: '#3b82f6'
    };
    return colors[rating] || '#888';
  };

  const getRatingIcon = (rating) => {
    const icons = {
      again: '✗',
      hard: '△',
      medium: '○',
      easy: '✓'
    };
    return icons[rating] || '?';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="statistics" onClick={(e) => e.stopPropagation()}>
        <div className="statistics-header">
          <h2>Statistics</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="statistics-content">
          {/* Deck Selector */}
          <div className="stat-section">
            <select
              className="deck-selector"
              value={selectedDeck}
              onChange={(e) => setSelectedDeck(e.target.value)}
            >
              <option value="all">All Decks</option>
              {Object.keys(allDecks).map(deckName => (
                <option key={deckName} value={deckName}>
                  {deckName}
                </option>
              ))}
            </select>
          </div>

          {/* Daily Streaks */}
          {selectedDeck === 'all' && (
            <StreakDisplay
              currentStreak={currentStreak}
              longestStreak={longestStreak}
              todayStudied={todayStudied}
            />
          )}

          {/* Study Goals */}
          {selectedDeck === 'all' && (
            <StudyGoals
              dailyGoal={dailyGoal}
              weeklyGoal={weeklyGoal}
              dailyProgress={dailyProgress}
              weeklyProgress={weeklyProgress}
              dailyGoalMet={dailyGoalMet}
              weeklyGoalMet={weeklyGoalMet}
              dailyProgressPercent={dailyProgressPercent}
              weeklyProgressPercent={weeklyProgressPercent}
              onSetDailyGoal={onSetDailyGoal}
              onSetWeeklyGoal={onSetWeeklyGoal}
            />
          )}

          {/* Overview Cards */}
          <div className="stat-cards">
            <div className="stat-card">
              <div className="stat-value">{displayStats.totalCards}</div>
              <div className="stat-label">Total Cards</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{displayStats.totalReviewed}</div>
              <div className="stat-label">Cards Studied</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{displayStats.totalReviewCount}</div>
              <div className="stat-label">Total Reviews</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{displayStats.accuracy}%</div>
              <div className="stat-label">Accuracy</div>
            </div>
          </div>

          {/* SRS Status */}
          {selectedDeck === 'all' && (
            <div className="stat-section">
              <h3>Study Status</h3>
              <div className="srs-status">
                <div className="srs-item srs-new">
                  <div className="srs-count">{stats.newCards}</div>
                  <div className="srs-label">New</div>
                </div>
                <div className="srs-item srs-learning">
                  <div className="srs-count">{stats.learningCards}</div>
                  <div className="srs-label">Learning</div>
                </div>
                <div className="srs-item srs-mature">
                  <div className="srs-count">{stats.matureCards}</div>
                  <div className="srs-label">Mature</div>
                </div>
                <div className="srs-item srs-due">
                  <div className="srs-count">{stats.dueCards}</div>
                  <div className="srs-label">Due Today</div>
                </div>
              </div>
            </div>
          )}

          {/* Review Forecast */}
          {selectedDeck === 'all' && (
            <ReviewForecast
              reviewData={reviewData}
              allDecks={allDecks}
            />
          )}

          {/* Rating Distribution */}
          <div className="stat-section">
            <h3>Rating Distribution</h3>
            <div className="rating-bars">
              {Object.entries(displayStats.ratingCounts).map(([rating, count]) => {
                const percentage = displayStats.totalReviewCount > 0
                  ? (count / displayStats.totalReviewCount * 100)
                  : 0;

                return (
                  <div key={rating} className="rating-bar-container">
                    <div className="rating-label">
                      <span className="rating-icon" style={{ color: getRatingColor(rating) }}>
                        {getRatingIcon(rating)}
                      </span>
                      <span className="rating-name">{rating.charAt(0).toUpperCase() + rating.slice(1)}</span>
                      <span className="rating-count">{count}</span>
                    </div>
                    <div className="rating-bar-track">
                      <div
                        className="rating-bar-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: getRatingColor(rating)
                        }}
                      />
                    </div>
                    <div className="rating-percentage">{percentage.toFixed(0)}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Deck Breakdown */}
          {selectedDeck === 'all' && Object.keys(stats.deckStats).length > 0 && (
            <div className="stat-section">
              <h3>Deck Breakdown</h3>
              <div className="deck-breakdown">
                {Object.entries(stats.deckStats).map(([deckName, deckStat]) => (
                  <div key={deckName} className="deck-stat-item">
                    <div className="deck-stat-header">
                      <span className="deck-name">{deckName}</span>
                      <span className="deck-accuracy">{deckStat.accuracy}%</span>
                    </div>
                    <div className="deck-stat-details">
                      <span>{deckStat.reviewed}/{deckStat.total} studied</span>
                      <span>{deckStat.due} due</span>
                      <span>{deckStat.reviewCount} reviews</span>
                    </div>
                    <div className="deck-progress-bar">
                      <div
                        className="deck-progress-fill"
                        style={{
                          width: `${(deckStat.reviewed / deckStat.total * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collections */}
          {selectedDeck === 'all' && (
            <div className="stat-section">
              <h3>Collections</h3>
              <div className="collections">
                <div className="collection-item">
                  <span className="collection-icon">⭐</span>
                  <span className="collection-label">Favorites</span>
                  <span className="collection-count">{stats.favoritesCount}</span>
                </div>
                <div className="collection-item">
                  <span className="collection-icon">✓</span>
                  <span className="collection-label">Memorized</span>
                  <span className="collection-count">{stats.memorizedCount}</span>
                </div>
              </div>
            </div>
          )}

          {/* Achievements */}
          {selectedDeck === 'all' && achievements && (
            <div className="stat-section">
              <h3>Achievements ({unlockedCount}/{totalCount})</h3>
              <AchievementsList
                achievements={achievements}
                unlockedAchievements={unlockedAchievements}
                getProgress={getProgress}
              />
            </div>
          )}

          {/* Progress Charts */}
          {selectedDeck === 'all' && (
            <ProgressCharts
              reviewData={reviewData}
              allDecks={allDecks}
            />
          )}

          {/* Recent Activity */}
          {selectedDeck === 'all' && recentReviews.length > 0 && (
            <div className="stat-section">
              <h3>Recent Activity</h3>
              <div className="recent-reviews">
                {recentReviews.map((item, idx) => (
                  <div key={idx} className="review-item">
                    <div className="review-header">
                      <span
                        className="review-rating"
                        style={{ color: getRatingColor(item.review.rating) }}
                      >
                        {getRatingIcon(item.review.rating)}
                      </span>
                      <span className="review-deck">{item.deckName}</span>
                      <span className="review-time">{formatDate(item.timestamp)}</span>
                    </div>
                    <div className="review-card">
                      <span className="review-ar">{item.card.ar}</span>
                      <span className="review-separator">→</span>
                      <span className="review-en">{item.card.en}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
